import { TGetAnalyticsData } from "@api/analytics";
import { ScrollArea } from "@components/ui/scroll-area";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { ANALYTICS_QUERY, type ReaderType } from "@pages/analytics/types";
import { memo, useMemo } from "react";
import { useIntersectionObserver, useWindowSize } from "usehooks-ts";
import { ErrorStateMessage } from "../error-state-message";
import { SessionTableRowSkeleton } from "./session-table-row-skeleton";
import { SessionsTableEmptyState } from "./sessions-table-empty-state";
import { SessionsTableHeader } from "./sessions-table-header";
import { SessionsTableRow } from "./sessions-table-row";

const SKELETON_COUNT = 7;

const parseTime = (timeStr: string): number => {
  if (!timeStr) return 0;

  // Handle colon-separated format: "HH:MM:SS" or "MM:SS"
  const colonMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (colonMatch) {
    const [, hoursOrMinutes, minutesOrSeconds, seconds] = colonMatch;
    // If there are 3 parts, it's HH:MM:SS format
    if (seconds !== undefined) {
      return (
        parseInt(hoursOrMinutes, 10) * 3600 +
        parseInt(minutesOrSeconds, 10) * 60 +
        parseInt(seconds, 10)
      );
    }
    // Otherwise it's MM:SS format
    return parseInt(hoursOrMinutes, 10) * 60 + parseInt(minutesOrSeconds, 10);
  }

  // Handle letter-based format: "5m 30s", "1h 2m", etc.
  const hours = timeStr.match(/(\d+)h/)?.[1] || "0";
  const minutes = timeStr.match(/(\d+)m/)?.[1] || "0";
  const secs = timeStr.match(/(\d+)s/)?.[1] || "0";
  return (
    parseInt(hours, 10) * 3600 + parseInt(minutes, 10) * 60 + parseInt(secs, 10)
  );
};

const getSortValue = (
  session: ReaderType,
  sortBy: string,
): string | number | { primary: number; secondary: number } => {
  switch (sortBy) {
    case "reader":
      // If reader has a name, sort by name alphabetically
      // If no name, sort by visitor_index numerically, then by session_index
      if (session.reader_name) {
        return session.reader_name.toLowerCase();
      }
      // Return an object with primary (visitor_index) and secondary (session_index) sort values
      return {
        primary: session.visitor_index,
        secondary: session.session_index,
      };
    case "session-started":
      return session.timestamp ? new Date(session.timestamp).getTime() : 0;
    case "reading-time":
      return parseTime(session.duration || "");
    case "clicked-link":
      return session.click_cta ? 1 : 0;
    case "progress":
      return (session.last_slide / session.slides_count) * 100;
    case "location":
      return `${session.city || ""} ${session.country || ""}`.toLowerCase();
    case "device":
      return (session.device || "").toLowerCase();
    case "recently-read":
      return session.timestamp ? new Date(session.timestamp).getTime() : 0;
    default:
      return 0;
  }
};

const compareValues = (
  aValue: string | number | { primary: number; secondary: number },
  bValue: string | number | { primary: number; secondary: number },
  sortDirection: string,
): number => {
  // Handle null/undefined values - push them to the end
  if (aValue === null || aValue === undefined) return 1;
  if (bValue === null || bValue === undefined) return -1;

  // Handle object values with primary and secondary sort
  if (
    typeof aValue === "object" &&
    typeof bValue === "object" &&
    "primary" in aValue &&
    "primary" in bValue
  ) {
    const primaryComparison = aValue.primary - bValue.primary;
    if (primaryComparison !== 0) {
      return sortDirection === "asc" ? primaryComparison : -primaryComparison;
    }
    // If primary values are equal, sort by secondary
    const secondaryComparison = aValue.secondary - bValue.secondary;
    return sortDirection === "asc" ? secondaryComparison : -secondaryComparison;
  }

  // If one is object and other is not (mixing named and numbered readers)
  if (typeof aValue === "object" && "primary" in aValue) {
    // Numbered readers (objects) come after named readers (strings) when ascending
    return sortDirection === "asc" ? 1 : -1;
  }
  if (typeof bValue === "object" && "primary" in bValue) {
    return sortDirection === "asc" ? -1 : 1;
  }

  // Ensure type consistency for comparison
  let comparison: number;
  if (typeof aValue === "number" && typeof bValue === "number") {
    comparison = aValue - bValue;
  } else {
    // Convert both to strings for comparison
    const aStr = String(aValue);
    const bStr = String(bValue);
    comparison = aStr.localeCompare(bStr);
  }

  // Apply sort direction
  return sortDirection === "asc" ? comparison : -comparison;
};

const sortSessions = (
  sessions: ReaderType[],
  sortBy: string,
  sortDirection: string,
): ReaderType[] => {
  if (!sessions.length) return sessions;

  return [...sessions].sort((a, b) => {
    const aValue = getSortValue(a, sortBy);
    const bValue = getSortValue(b, sortBy);
    return compareValues(aValue, bValue, sortDirection);
  });
};

const filterSessions = (
  sessions: ReaderType[],
  searchTerm: string,
): ReaderType[] => {
  if (!searchTerm.trim()) {
    return sessions;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return sessions.filter((session) => {
    const readerName = (
      session.reader_name || `Reader #${session.visitor_index}`
    ).toLowerCase();
    const readerEmail = (session.reader_email || "").toLowerCase();
    const readerPosition = (session.reader_position || "").toLowerCase();
    const location =
      `${session.city || ""} ${session.country || ""}`.toLowerCase();

    return (
      readerName.includes(lowerSearchTerm) ||
      readerEmail.includes(lowerSearchTerm) ||
      readerPosition.includes(lowerSearchTerm) ||
      location.includes(lowerSearchTerm)
    );
  });
};

export const SessionsTableWrapper = memo(
  ({
    sortBy,
    sortDirection,
    searchTerm,
    overrides,
    isOwner,
  }: {
    sortBy: string;
    sortDirection: string;
    searchTerm: string;
    overrides: Partial<TGetAnalyticsData["params"]>;
    isOwner: boolean;
  }) => {
    const { isIntersecting, ref } = useIntersectionObserver({
      threshold: 0.1,
    });
    const {
      data: response,
      isLoading,
      isError,
    } = useAnalyticsQuery({
      query: [ANALYTICS_QUERY.READERS],
      overrides: overrides || {},
      enabled: isIntersecting,
    });

    const sessions = useMemo(() => {
      const sessionsData = response?.[ANALYTICS_QUERY.READERS] as
        | ReaderType[]
        | undefined;
      return sessionsData || [];
    }, [response]);

    const filteredSessions = useMemo(
      () => filterSessions(sessions, searchTerm),
      [sessions, searchTerm],
    );

    const sortedSessions = useMemo(
      () => sortSessions(filteredSessions, sortBy, sortDirection),
      [filteredSessions, sortBy, sortDirection],
    );

    const hasData = sortedSessions.length > 0;

    // For now, using placeholder values for pageTitle, versionTitle, etc.
    // These would come from the actual data or context in a real implementation
    const pageTitle = "Page Title";
    const versionTitle = "Version Title";
    const storyId = "";
    const versionId = "";

    return (
      <div
        ref={ref}
        className="tw-shadow-2xs tw-rounded-xl tw-outline tw-outline-1 tw-grid tw-outline-blue/5 tw-bg-white tw-overflow-hidden"
      >
        <div className="tw-overflow-x-auto tw-max-w-full">
          <div className="tw-min-w-[900px]">
            <SessionsTableHeader />

            {isError && <ErrorStateMessage />}

            {isLoading &&
              Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <SessionTableRowSkeleton key={index} />
              ))}

            {!isLoading && !isError && !hasData && <SessionsTableEmptyState />}

            {!isLoading && !isError && hasData && (
              <ScrollAreaWrapper>
                {sortedSessions.map((session) => (
                  <SessionsTableRow
                    key={session.session_id}
                    session={session}
                    pageTitle={pageTitle}
                    versionTitle={versionTitle}
                    storyId={storyId}
                    versionId={versionId}
                    allSessions={sessions}
                    isOwner={isOwner}
                  />
                ))}
              </ScrollAreaWrapper>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const ScrollAreaWrapper = ({ children }: { children: React.ReactNode }) => {
  const { width } = useWindowSize();
  const isMobile = width <= 1300;
  if (isMobile) {
    return (
      <div className="tw-overflow-y-auto tw-max-h-[423px]">{children}</div>
    );
  }
  return <ScrollArea maxHeight={423}>{children}</ScrollArea>;
};
